import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { fmtDateTime as fmtDate, fmtMoney } from "@/lib/intl";
import CriticalAlertsStrip from "@/components/CriticalAlertsStrip";

function isDateField(key) {
  return /(date|time|At|datetime|endTime)$/i.test(key);
}

function isMoneyField(key) {
  return /(price|amount|paid|tax|total)/i.test(key);
}

function renderValue(key, val) {
  if (val === null || val === undefined || val === "") return <span className="text-zinc-400">—</span>;
  if (typeof val === "boolean") {
    return (
      <Badge variant={val ? "default" : "outline"} className={val ? "bg-zinc-900 text-white" : ""}>
        {val ? "true" : "false"}
      </Badge>
    );
  }
  if (Array.isArray(val) || typeof val === "object") {
    return (
      <pre className="font-mono-num text-xs bg-zinc-50 border border-zinc-200 rounded p-2 whitespace-pre-wrap break-all">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  if (key && isDateField(key) && typeof val === "string" && val.length >= 10) {
    return <span className="font-mono-num">{fmtDate(val)}</span>;
  }
  if (key && isMoneyField(key)) {
    return <span className="font-mono-num">{fmtMoney(val)}</span>;
  }
  if (typeof val === "number") {
    return <span className="font-mono-num">{val}</span>;
  }
  return <span className="break-all">{String(val)}</span>;
}

function FieldRow({ k, v }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 py-2 border-b border-zinc-100 last:border-0">
      <div className="text-2xs font-semibold uppercase tracking-wider text-zinc-500 sm:pt-0.5">
        {k}
      </div>
      <div className="sm:col-span-2 text-sm text-zinc-900">{renderValue(k, v)}</div>
    </div>
  );
}

export default function AppointmentDetails({ appointment, open, onOpenChange }) {
  if (!appointment) return null;

  // Group fields for readability
  const groups = {
    Identity: ["id", "firstName", "lastName", "email", "phone"],
    Schedule: [
      "datetime",
      "date",
      "time",
      "endTime",
      "duration",
      "timezone",
      "calendarTimezone",
    ],
    Service: [
      "type",
      "appointmentTypeID",
      "category",
      "calendar",
      "calendarID",
      "classID",
      "location",
      "addonIDs",
    ],
    Financial: ["price", "paid", "amountPaid", "certificate"],
    Status: ["canceled", "canceledAt", "noShow", "scheduledBy", "labels"],
    Other: ["notes", "confirmationPage", "conferenceLink", "canClientCancel", "canClientReschedule", "formsText"],
  };

  const grouped = new Set();
  Object.values(groups).forEach((arr) => arr.forEach((k) => grouped.add(k)));
  const remaining = Object.keys(appointment).filter(
    (k) => !grouped.has(k) && k !== "forms"
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="appointment-details-dialog"
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {[appointment.firstName, appointment.lastName].filter(Boolean).join(" ") ||
              `Appointment #${appointment.id}`}
          </DialogTitle>
          <DialogDescription className="font-mono-num text-xs">
            #{appointment.id} · {fmtDate(appointment.datetime)} · {appointment.type || "—"}
          </DialogDescription>
        </DialogHeader>

        {appointment.email && (
          <div className="pb-2">
            <CriticalAlertsStrip email={appointment.email} />
          </div>
        )}

        <Tabs defaultValue="organized" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-zinc-100">
            <TabsTrigger value="organized" data-testid="details-tab-organized">
              Organized
            </TabsTrigger>
            <TabsTrigger value="forms" data-testid="details-tab-forms">
              Custom forms
              {appointment.forms?.length ? (
                <span className="ml-1.5 text-2xs font-mono-num text-zinc-500">
                  {appointment.forms.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="raw" data-testid="details-tab-raw">
              Raw JSON
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organized" className="overflow-y-auto pr-1 thin-scroll">
            <div className="space-y-6 mt-4">
              {Object.entries(groups).map(([title, keys]) => {
                const present = keys.filter((k) => k in appointment);
                if (!present.length) return null;
                return (
                  <section key={title}>
                    <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                      {title}
                    </div>
                    <div className="border border-zinc-200 rounded-md bg-white px-4">
                      {present.map((k) => (
                        <FieldRow key={k} k={k} v={appointment[k]} />
                      ))}
                    </div>
                  </section>
                );
              })}
              {remaining.length > 0 && (
                <section>
                  <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    Additional fields
                  </div>
                  <div className="border border-zinc-200 rounded-md bg-white px-4">
                    {remaining.map((k) => (
                      <FieldRow key={k} k={k} v={appointment[k]} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </TabsContent>

          <TabsContent value="forms" className="overflow-y-auto pr-1 thin-scroll">
            <div className="mt-4 space-y-4">
              {(!appointment.forms || appointment.forms.length === 0) && (
                <div className="text-sm text-zinc-500 border-2 border-dashed border-zinc-200 rounded-md p-8 text-center bg-zinc-50">
                  No custom forms attached to this appointment.
                </div>
              )}
              {(appointment.forms || []).map((form) => (
                <section
                  key={form.id}
                  className="border border-zinc-200 rounded-md bg-white"
                >
                  <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <div className="font-medium text-zinc-900">{form.name}</div>
                    <div className="font-mono-num text-xs text-zinc-500">#{form.id}</div>
                  </div>
                  <div className="px-4">
                    {(form.values || []).map((v, i) => (
                      <FieldRow key={i} k={v.name || v.fieldID} v={v.value} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="raw" className="overflow-y-auto pr-1 thin-scroll">
            <pre
              data-testid="details-raw-json"
              className="mt-4 text-xs font-mono-num bg-zinc-950 text-zinc-100 p-4 rounded-md overflow-auto"
            >
              {JSON.stringify(appointment, null, 2)}
            </pre>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
